"use client"

import { Session, User } from "@supabase/supabase-js"
import { createContext, useContext, useEffect, useState } from "react"

import { BASE_PATH } from "@/constants/base-url"
import { useQotdPendingFlush } from "@/lib/qotd/flush"
import { createClient } from "@/lib/supabase"

export interface AuthContextType {
  loading: boolean
  session: null | Session
  signInWithGoogle: (returnUrl?: string) => Promise<void>
  signInWithMagicLink: (email: string, returnUrl?: string) => Promise<void>
  signOut: () => Promise<void>
  user: null | User
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<null | User>(null)
  const [session, setSession] = useState<null | Session>(null)
  const [loading, setLoading] = useState(true)
  const supabase = createClient()
  const flushQotdPending = useQotdPendingFlush()

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const {
        data: { session }
      } = await supabase.auth.getSession()
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    }

    getInitialSession()

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Clear chat history when user signs in for security
      if (event === "SIGNED_IN") {
        Object.keys(sessionStorage)
          .filter((key) => key.startsWith("ai-chat-storage-"))
          .forEach((key) => sessionStorage.removeItem(key))
      }

      if (event === "INITIAL_SESSION" || event === "SIGNED_IN") {
        // Only attempt to flush pending QOTD attempts if user is actually logged in
        if (session?.user) {
          void flushQotdPending()
        }
      }

      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [supabase.auth, flushQotdPending])

  /** Store return URL in a cookie */
  const rememberReturnUrl = (returnUrl?: string) => {
    if (returnUrl) {
      document.cookie = `authReturnUrl=${encodeURIComponent(
        returnUrl
      )}; path=/; max-age=3600; SameSite=Lax`
    }
  }

  const signInWithMagicLink = async (email: string, returnUrl?: string) => {
    rememberReturnUrl(returnUrl)

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}${BASE_PATH}/auth/callback`
      }
    })
    if (error) throw error
  }

  const signInWithGoogle = async (returnUrl?: string) => {
    rememberReturnUrl(returnUrl)

    const { error } = await supabase.auth.signInWithOAuth({
      options: {
        queryParams: {
          access_type: "offline",
          prompt: "consent"
        },
        redirectTo: `${window.location.origin}${BASE_PATH}/auth/callback`,
        scopes: "email profile"
      },
      provider: "google"
    })
    if (error) throw error
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  const value = {
    loading,
    session,
    signInWithGoogle,
    signInWithMagicLink,
    signOut,
    user
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
